import React, { useEffect } from "react";
import { Download, Smartphone, X, Share } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSite } from "../../store/useSite";
import { usePwaStore } from "../../store/usePwaStore";
import environment from "../../environments/environment";

/**
 * SidebarPwaInstall
 * An ultra-clean, tactile PWA install component tailored to match the event theme.
 */
export const SidebarPwaInstall = ({ dark = false, onActionComplete }) => {
  const location = useLocation();
  const isDashboard = location.pathname.includes("/dashboard");

  const eventData = useSite((state) => state.eventData);
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentSlug =
    eventData?.slug || (pathParts.length > 0 && !isDashboard ? pathParts[0] : "");
  const eventName = eventData?.event_name || eventData?.name || "Event";

  const {
    isStandalone,
    isInstalled,
    isIos,
    showIosModal,
    setShowIosModal,
    initPwaListeners,
    promptInstall,
  } = usePwaStore();

  useEffect(() => {
    const cleanup = initPwaListeners();
    return cleanup;
  }, [initPwaListeners]);

  // Icon URL priority: pwa_icon -> backend icon route -> default logo512
  const apiBase = environment.apiUrl || "";
  const iconSrc = eventData?.pwa_icon
    ? eventData.pwa_icon.startsWith("http")
      ? eventData.pwa_icon
      : `${apiBase}/${eventData.pwa_icon.replace(/^\//, "")}`
    : currentSlug
      ? `${apiBase}/manifest/icon/${currentSlug}`
      : "/logo512.png";

  if (isStandalone || isInstalled || isDashboard) {
    return null;
  }

  const handleInstallClick = async (e) => {
    e.stopPropagation();
    const res = await promptInstall();
    if (res?.outcome === "accepted" && onActionComplete) {
      onActionComplete();
    }
  };

  return (
    <div className="w-full mt-auto pt-4 pb-3 px-3.5">
      {/* Event Theme Native Card */}
      <div
        onClick={handleInstallClick}
        className={`group relative overflow-hidden rounded-xl p-3 border transition-all duration-200 cursor-pointer ${
          dark
            ? "bg-[#0d1527] border-white/10 text-white hover:border-white/20 shadow-sm"
            : "bg-[#F8FAFC] border-slate-200/90 text-slate-900 hover:border-slate-300 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.06)]"
        }`}
      >
        <div className="flex items-center gap-3">
          {/* App / Event Icon */}
          <div className="relative shrink-0">
            <div
              className={`w-11 h-11 rounded-xl overflow-hidden border flex items-center justify-center ${
                dark ? "bg-white/5 border-white/10" : "bg-white border-slate-200/80 shadow-xs"
              }`}
            >
              <img
                src={iconSrc}
                alt={eventName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo512.png";
                }}
              />
            </div>
          </div>

          {/* Typography */}
          <div className="flex-1 min-w-0 pr-1">
            <div className="flex items-center gap-1.5">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider ${
                  dark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                Mobile App
              </span>
            </div>
            <h4
              className={`text-[13px] font-bold leading-tight truncate tracking-tight ${
                dark ? "text-white" : "text-slate-900"
              }`}
            >
              Install {eventName}
            </h4>
            <p
              className={`text-[11px] font-normal leading-tight truncate mt-0.5 ${
                dark ? "text-slate-400" : "text-slate-500"
              }`}
            >
              Add to Home Screen
            </p>
          </div>

          {/* Draw Outline CTA Button */}
          <button
            type="button"
            onClick={handleInstallClick}
            className={`draw-outline-btn shrink-0 relative overflow-hidden inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm tracking-wide transition-all duration-200 active:scale-[0.96] shadow-sm ${
              dark
                ? "bg-white text-slate-950 hover:bg-slate-100"
                : "bg-slate-950 text-white hover:bg-slate-800"
            }`}
          >
            {/* Draw Outline SVG Stroke on Hover */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none rounded-xl overflow-visible"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="1.5"
                y="1.5"
                width="calc(100% - 3px)"
                height="calc(100% - 3px)"
                rx="10"
                ry="10"
                pathLength="100"
                className={`draw-outline-path fill-none stroke-[2px] ${
                  dark ? "stroke-indigo-500" : "stroke-blue-500"
                }`}
              />
            </svg>

            {/* Smooth light sweep shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2.5s_infinite]" />
            <Download size={15} className="shrink-0 relative z-10 transition-transform duration-200 group-hover:translate-y-0.5" />
            <span className="relative z-10">Install</span>
          </button>
        </div>
      </div>

      {/* iOS / Step-by-Step Instruction Dialog */}
      {showIosModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setShowIosModal(false)}
        >
          <div
            className="bg-white text-slate-900 w-full max-w-sm rounded-2xl shadow-2xl p-5 border border-slate-100 space-y-4 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-50 flex items-center justify-center">
                  <img
                    src={iconSrc}
                    alt={eventName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/logo512.png";
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-900 leading-tight">
                    Install {eventName}
                  </h3>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Add to your device Home Screen
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowIosModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2.5 text-xs text-slate-600">
              {isIos ? (
                <>
                  <p className="font-medium text-slate-700">
                    Follow these 2 steps in Safari:
                  </p>
                  <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="w-7 h-7 rounded-lg bg-white shadow-xs border border-slate-200 text-slate-800 flex items-center justify-center shrink-0">
                      <Share size={14} />
                    </div>
                    <span>
                      1. Tap the <strong>Share</strong> button in Safari menu.
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200/70">
                    <div className="w-7 h-7 rounded-lg bg-white shadow-xs border border-slate-200 text-slate-800 flex items-center justify-center shrink-0 font-bold">
                      ➕
                    </div>
                    <span>
                      2. Scroll and tap <strong>Add to Home Screen</strong>.
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium text-slate-700">
                    To install on your mobile device:
                  </p>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 space-y-1">
                    <p>1. Open your browser menu ( ⋮ or ⋯ ).</p>
                    <p>2. Tap <strong>Install App</strong> or <strong>Add to Home Screen</strong>.</p>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowIosModal(false)}
              className="w-full py-2.5 bg-slate-950 hover:bg-slate-800 text-white font-bold rounded-xl text-xs shadow-xs transition-colors active:scale-[0.98]"
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarPwaInstall;
